/**
 * This week's drop, as a card. On Home it leads the page until the coach
 * opens the drop or closes the card with its X (Coleman, 2026-10-22: "the
 * weekly drop should persist until they see it or x it out. it's immensley
 * important"), as the front card of the Next Up stack (`flush`, the rest
 * of the stack tucked under it); after that, Home shows DropNextLine. Learn
 * shows the same card without the X.
 *
 * Its own look, so it never reads as one more to-do: gold edge and wash,
 * three of the drop's covers fanned like a hand of cards on the right
 * (Coleman picked the fan, 2026-10-22; the card keeps its old height), a
 * live dot while it's unopened, and
 * "What's inside" to see the drop before opening it. Renders nothing when
 * there's no drop.
 */
import React, { useState } from "react";
import { ArrowRight, Check, ChevronDown, Gift, Star, X } from "lucide-react";
import { GOLD } from "../constants/theme";
import { fmtDropDate, dropFocus, dropCounts, nextDropSlot, SECTIONS, sectionLabel, DROP_TZ, featuredItem, sectionOrder } from "./dropModel";
import { itemThumb } from "./dropThumb";

// The covers worth leading with: the starred item first, then the science, the masterclass
// and the X&O before the drills. Three fan out on a wide screen; a phone gets a row of up to four.
const ART_ORDER = ["science", "masterclass", "xo", "package", "research", "drills"];
const coversOf = (items) => {
  const star = featuredItem(items);
  return [...(items || [])]
    .filter(i => itemThumb(i))
    .sort((a, b) => (b === star) - (a === star) || ART_ORDER.indexOf(a.section) - ART_ORDER.indexOf(b.section))
    .slice(0, 4)
    .map(itemThumb);
};

const CSS = `
.bam-drop-card{transition:transform .35s cubic-bezier(.2,.8,.2,1),box-shadow .35s cubic-bezier(.2,.8,.2,1)}
.bam-drop-fan{position:relative;width:300px;height:150px;flex-shrink:0;align-self:center;padding:0;border:none;background:none;cursor:pointer}
.bam-drop-fan img{position:absolute;left:66px;top:28px;width:168px;height:95px;object-fit:cover;border-radius:10px;
  border:1px solid rgba(255,255,255,.16);box-shadow:0 12px 28px rgba(0,0,0,.6);background:#000;
  transform:translate(var(--x),var(--y)) rotate(var(--r));transition:transform .5s cubic-bezier(.2,.8,.2,1)}
.bam-drop-strip{display:flex;gap:8px;overflow-x:auto;scrollbar-width:none;margin:2px -16px 0;padding:0 16px}
.bam-drop-strip::-webkit-scrollbar{display:none}
.bam-drop-strip img{width:112px;height:63px;flex-shrink:0;object-fit:cover;border-radius:8px;border:1px solid rgba(255,255,255,.12);background:#000}
@media (hover:hover){.bam-drop-card:hover{transform:translateY(-2px);box-shadow:0 18px 44px rgba(0,0,0,.45)}
  .bam-drop-card:hover .bam-drop-fan img{transform:translate(calc(var(--x) * 1.3),calc(var(--y) - 6px)) rotate(calc(var(--r) * .55))}}
.bam-drop-live{width:7px;height:7px;border-radius:50%;background:${GOLD};flex-shrink:0;animation:bamDropLive 1.6s ease-out infinite}
@keyframes bamDropLive{0%{box-shadow:0 0 0 0 ${GOLD}88}70%{box-shadow:0 0 0 7px ${GOLD}00}100%{box-shadow:0 0 0 0 ${GOLD}00}}
@media (prefers-reduced-motion:reduce){.bam-drop-card,.bam-drop-fan img{transition:none}.bam-drop-live{animation:none}}
`;

export default function DropCard({ C, drop, go, narrow = false, eyebrow = "Wednesday Drop", pending = false, thisWeek = true, flush = false, onClose }) {
  const [inside, setInside] = useState(false);
  if (!drop) return null;
  const focus = dropFocus(drop);
  const counts = dropCounts(drop.items || []);
  const covers = coversOf(drop.items);
  const star = featuredItem(drop.items);
  const open = () => go("drop");
  return (
    <div className="bam-drop-card" style={{ position: "relative", borderRadius: 16, overflow: "hidden", marginBottom: flush ? 0 : narrow ? 14 : 18,
      border: `1px solid ${GOLD}55`, background: `linear-gradient(135deg, ${GOLD}17 0%, transparent 55%), ${C.cardGradient}`,
      boxShadow: `0 0 0 3px ${GOLD}12, 0 10px 40px rgba(0,0,0,.4)`, color: C.text }}>
      <style>{CSS}</style>
      {onClose && (
        <button type="button" onClick={onClose} aria-label="Close this week's drop" title="Close"
          style={{ position: "absolute", top: 12, right: 12, zIndex: 2, width: 32, height: 32, borderRadius: 16, cursor: "pointer",
            border: `1px solid ${C.cardBorder}`, background: "rgba(10,10,10,0.7)", color: C.textMid,
            display: "flex", alignItems: "center", justifyContent: "center" }}>
          <X size={15} strokeWidth={2.3}/>
        </button>
      )}
      <div style={{ display: "flex", flexDirection: narrow ? "column" : "row", alignItems: "stretch" }}>
        <div style={{ flex: 1, minWidth: 0, padding: narrow ? "16px 16px 18px" : "22px 26px", paddingRight: onClose && !narrow ? 56 : undefined,
          display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 10.5, fontWeight: 800, letterSpacing: 1.5,
            textTransform: "uppercase", color: GOLD, paddingRight: onClose && narrow ? 40 : 0 }}>
            {pending && <span className="bam-drop-live" aria-label="New"/>}
            <Gift size={13}/> {eyebrow} · {fmtDropDate(drop.publish_at)}
          </div>
          <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: narrow ? 30 : 38, lineHeight: 1, letterSpacing: 0.4 }}>{thisWeek ? "This week's drop" : "The latest drop"}</div>
          {counts.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {counts.map(c => (
                <span key={c} style={{ fontSize: 11.5, fontWeight: 700, color: C.text, border: `1px solid ${C.cardBorder}`,
                  borderRadius: 12, padding: "3px 9px", background: "rgba(255,255,255,0.03)" }}>{c}</span>
              ))}
            </div>
          )}
          {star?.snapshot?.title && (
            <div style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 13.5, color: C.text, fontWeight: 700, minWidth: 0 }}>
              <Star size={13} fill={GOLD} color={GOLD} style={{ flexShrink: 0 }}/>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{star.snapshot.title}</span>
            </div>
          )}
          {narrow && covers.length > 0 && (
            <div className="bam-drop-strip" aria-hidden="true">{covers.map((src, i) => <img key={i} src={src} alt="" loading="lazy"/>)}</div>
          )}
          {focus && (
            <div style={{ fontSize: 13.5, color: C.textMid, lineHeight: 1.5 }}>
              <span style={{ color: GOLD, fontWeight: 800 }}>Focus: </span><span style={{ color: C.text, fontWeight: 700 }}>{focus}</span>
            </div>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginTop: 4 }}>
            <button type="button" onClick={open}
              style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "0 20px", minHeight: 44, borderRadius: 22, border: "none",
                background: GOLD, color: "#111", fontWeight: 800, fontSize: 14, fontFamily: "inherit", cursor: "pointer", whiteSpace: "nowrap" }}>
              {thisWeek ? "Open this week's drop" : "Open the drop"} <ArrowRight size={15}/>
            </button>
            {(drop.items || []).length > 0 && (
              <button type="button" onClick={() => setInside(v => !v)} aria-expanded={inside}
                style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "0 14px", minHeight: 44, borderRadius: 22,
                  border: `1px solid ${C.cardBorder}`, background: "transparent", color: C.text, fontWeight: 700, fontSize: 13, fontFamily: "inherit", cursor: "pointer" }}>
                What's inside <ChevronDown size={14} style={{ transform: inside ? "rotate(180deg)" : "none", transition: "transform .2s" }}/>
              </button>
            )}
          </div>
          {inside && <Inside items={drop.items} C={C} onOpen={open}/>}
        </div>
        {!narrow && covers.length > 0 && <Fan covers={covers.slice(0, 3)} onOpen={open} roomForX={!!onClose}/>}
      </div>
    </div>
  );
}

/** Three covers fanned like a hand of cards; the first (the science, as a rule) in front. They spread on hover. */
const FAN = [{ x: "0px", y: "-6px", r: "0deg", z: 3 }, { x: "-74px", y: "10px", r: "-9deg", z: 2 }, { x: "74px", y: "10px", r: "9deg", z: 1 }];
function Fan({ covers, onOpen, roomForX }) {
  return (
    <button type="button" onClick={onOpen} tabIndex={-1} aria-hidden="true" className="bam-drop-fan" style={{ marginRight: roomForX ? 56 : 22 }}>
      {covers.map((src, i) => {
        const f = FAN[covers.length === 1 ? 0 : i];
        return <img key={i} src={src} alt="" loading="lazy" style={{ "--x": f.x, "--y": f.y, "--r": f.r, zIndex: f.z }}/>;
      })}
    </button>
  );
}

/** The drop's contents by section, a few titles each, so it's worth opening before it's opened. */
function Inside({ items, C, onOpen }) {
  const order = sectionOrder(items);
  const bySection = SECTIONS.slice().sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id))
    .map(s => ({ s, list: (items || []).filter(i => i.section === s.id) })).filter(x => x.list.length);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 6, paddingTop: 12, borderTop: `1px solid ${C.cardBorder}` }}>
      {bySection.map(({ s, list }) => {
        const titles = list.map(i => i.snapshot?.title).filter(Boolean);
        const shown = titles.slice(0, 3);
        return (
          <button key={s.id} type="button" onClick={onOpen}
            style={{ display: "flex", gap: 12, alignItems: "baseline", textAlign: "left", background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: "inherit", color: C.text }}>
            <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: 1.2, textTransform: "uppercase", color: C.textMid, minWidth: 172, flexShrink: 0 }}>{sectionLabel(s.id)}</span>
            <span style={{ fontSize: 13.5, lineHeight: 1.45 }}>
              {shown.join(" · ")}{titles.length > shown.length ? <span style={{ color: C.textMid }}> · +{titles.length - shown.length} more</span> : null}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/** Once this week's drop is opened or closed: a quiet line in its place, until the next one lands. */
export function DropNextLine({ C, drop, go, opened, thisWeek = true, now = new Date() }) {
  if (!drop) return null;
  const next = new Date(nextDropSlot(now)).toLocaleDateString("en-US", { timeZone: DROP_TZ, weekday: "long", month: "long", day: "numeric" });
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", padding: "11px 16px", marginBottom: 18, borderRadius: 14,
      border: `1px solid ${C.cardBorder}`, color: C.textMid, fontSize: 13 }}>
      <Gift size={14} color={GOLD}/>
      <span>{opened ? <><Check size={12} style={{ color: GOLD, verticalAlign: -1 }}/> {thisWeek ? "This week's drop" : "The latest drop"} opened</> : thisWeek ? "This week's drop" : "The latest drop"}</span>
      <span aria-hidden="true">·</span>
      <span>Next drop <b style={{ color: C.text }}>{next}</b></span>
      <button type="button" onClick={() => go("drop")}
        style={{ marginLeft: "auto", background: "none", border: "none", color: GOLD, fontWeight: 800, fontSize: 12.5, cursor: "pointer", fontFamily: "inherit", padding: 0 }}>
        {opened ? "Open it again" : "Open it"}
      </button>
    </div>
  );
}

/**
 * This week's drop as a slim bar across the top of every page but Home and
 * the drop itself (Coleman, 2026-10-22: lots of coaches never land on Home,
 * they go straight to Community or the drills). Same rule as Home's card: it
 * stays until the drop is opened or the X closes it, and closing either one
 * closes both.
 */
export function DropBar({ C, drop, go, onClose, narrow = false }) {
  if (!drop) return null;
  const star = featuredItem(drop.items);
  const what = star?.snapshot?.title || dropCounts(drop.items || []).join(" · ");
  return (
    <div role="region" aria-label="This week's drop" className="bam-drop-bar"
      style={{ display: "flex", alignItems: "center", gap: 12, padding: narrow ? "8px 12px" : "9px 28px", flexShrink: 0,
        background: `linear-gradient(90deg, ${GOLD}22 0%, ${GOLD}0A 60%, transparent 100%)`, borderBottom: `1px solid ${GOLD}33`, color: C.text }}>
      <style>{CSS}</style>
      <span className="bam-drop-live" aria-hidden="true"/>
      <Gift size={15} color={GOLD} style={{ flexShrink: 0 }}/>
      <button type="button" onClick={() => go("drop")}
        style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "baseline", gap: 10, background: "none", border: "none", padding: 0,
          cursor: "pointer", fontFamily: "inherit", color: C.text, textAlign: "left" }}>
        <span style={{ fontSize: 13.5, fontWeight: 800, whiteSpace: "nowrap" }}>This week's drop is up</span>
        {what && !narrow && (
          <span style={{ fontSize: 13, color: C.textMid, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0, display: "inline-flex", alignItems: "baseline", gap: 6 }}>
            {star && <Star size={11} fill={GOLD} color={GOLD} style={{ flexShrink: 0, alignSelf: "center" }}/>}{what}
          </span>
        )}
      </button>
      <button type="button" onClick={() => go("drop")}
        style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "0 14px", minHeight: 32, borderRadius: 16, border: "none",
          background: GOLD, color: "#111", fontWeight: 800, fontSize: 12.5, fontFamily: "inherit", cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0 }}>
        Open it <ArrowRight size={13}/>
      </button>
      {onClose && (
        <button type="button" onClick={onClose} aria-label="Close this week's drop" title="Close"
          style={{ width: 30, height: 30, borderRadius: 15, border: "none", background: "transparent", color: C.textMid, cursor: "pointer",
            display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <X size={15} strokeWidth={2.3}/>
        </button>
      )}
    </div>
  );
}
